import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  LinearProgress,
  Checkbox,
  DialogContentText,
} from '@mui/material';
import {
  SmartToy,
  RecordVoiceOver,
  Add,
  Delete,
  Upload,
  Description
} from '@mui/icons-material';

const InterviewModeSelector = ({ open, onClose, interview, onModeSelected }) => {
  const [selectedMode, setSelectedMode] = useState('ai');
  const [customQuestions, setCustomQuestions] = useState(['']);
  const [questionType, setQuestionType] = useState('generate');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Instruction dialog states
  const [tempSessionData, setTempSessionData] = useState(null);
  const [showBegin, setShowBegin] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);
  const [understood, setUnderstood] = useState(false);

  // Reset all states when dialog closes or cancels
  const handleCancel = () => {
    // Reset all instruction-related states
    setShowBegin(false);
    setShowInstruction(false);
    setUnderstood(false);
    setTempSessionData(null);
    
    // Reset other states
    setSelectedMode('ai');
    setCustomQuestions(['']);
    setQuestionType('generate');
    setUploadedFile(null);
    setUploadError('');
    
    onClose();
  };

  const handleAddQuestion = () => {
    setCustomQuestions([...customQuestions, '']);
  };

  const handleRemoveQuestion = (index) => {
    if (customQuestions.length > 1) {
      setCustomQuestions(customQuestions.filter((_, i) => i !== index));
    }
  };

  const handleQuestionChange = (index, value) => {
    const updated = [...customQuestions];
    updated[index] = value;
    setCustomQuestions(updated);
  };

const handleStartClick = () => {
  let finalQuestions = customQuestions
    .map(q => q?.trim())
    .filter(q => q && q.length > 5);

  const sessionData = {
    interviewId: interview.interviewId,
    mode: selectedMode,
    customQuestions: finalQuestions.length > 0 ? finalQuestions : undefined,
    questionSource:
      selectedMode === 'ai'
        ? finalQuestions.length > 0
          ? 'uploaded_file'
          : 'ai_generated'
        : 'manual',
    positionApplied: interview.positionApplied,
    skills: interview.skills,
    experience: interview.experience,
    round: interview.round,
    preloadedQuestions: finalQuestions,
  };

  // ✅ ONLY show instructions for AI mode
  if (selectedMode === 'ai') {
    setTempSessionData(sessionData);
    setShowBegin(true);
  } else {
    // ✅ For manual mode, pass the session data and close
    onModeSelected(sessionData);
    // Don't call handleCancel() here - just close the dialog
    onClose(); // Just close without resetting
  }
};

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          Select Interview Mode - {interview.name}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Choose how to conduct this interview
          </Typography>

          <RadioGroup value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)}>
            {/* AI MODE */}
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                border: selectedMode === 'ai' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: selectedMode === 'ai' ? '#f3f8ff' : 'white'
              }}
            >
              <CardContent>
                <FormControlLabel
                  value="ai"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SmartToy sx={{ mr: 1, color: '#1976d2' }} />
                      <Typography variant="h6">AI-Powered Interview</Typography>
                      <Chip label="Recommended" color="primary" size="small" sx={{ ml: 2 }} />
                    </Box>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Automated Q&A with real-time evaluation
                </Typography>

                {selectedMode === 'ai' && (
                  <Box sx={{ ml: 4 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Question Source</InputLabel>
                      <Select
                        value={questionType}
                        label="Question Source"
                        onChange={(e) => setQuestionType(e.target.value)}
                      >
                        <MenuItem value="generate">AI-Generated</MenuItem>
                        <MenuItem value="custom">Upload Custom</MenuItem>
                      </Select>
                    </FormControl>

                    {questionType === 'custom' && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Upload Questions:
                        </Typography>
                        {/* File upload UI - simplified for demo */}
                        <Typography variant="caption" color="text.secondary">
                          Upload PDF/DOCX/TXT or add manually below
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                          Or add manually:
                        </Typography>
                        {customQuestions.map((q, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={`Question ${i + 1}`}
                              value={q}
                              onChange={(e) => handleQuestionChange(i, e.target.value)}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveQuestion(i)}
                              disabled={customQuestions.length === 1}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          startIcon={<Add />}
                          onClick={handleAddQuestion}
                          size="small"
                        >
                          Add Question
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* MANUAL MODE */}
            <Card
              variant="outlined"
              sx={{
                border: selectedMode === 'manual' ? '2px solid #ed6c02' : '1px solid #e0e0e0',
                backgroundColor: selectedMode === 'manual' ? '#fff4e5' : 'white'
              }}
            >
              <CardContent>
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <RecordVoiceOver sx={{ mr: 1, color: '#ed6c02' }} />
                      <Typography variant="h6">Manual Recording</Typography>
                    </Box>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Free-form conversation with recording
                </Typography>
              </CardContent>
            </Card>
          </RadioGroup>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            variant="contained"
            color={selectedMode === 'ai' ? 'primary' : 'warning'}
            startIcon={selectedMode === 'ai' ? <SmartToy /> : <RecordVoiceOver />}
            onClick={handleStartClick}
          >
            {`Start ${selectedMode === 'ai' ? 'AI' : 'Manual'} Interview`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* BEGIN DIALOG - Only for AI Mode */}
      <Dialog open={showBegin} onClose={() => {
        setShowBegin(false);
        setUnderstood(false);
        setTempSessionData(null);
      }} maxWidth="xs" fullWidth>
        <DialogTitle>Ready to start?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Click <strong>Begin</strong> to read the interview instructions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowBegin(false);
            setUnderstood(false);
            setTempSessionData(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowBegin(false);
              setShowInstruction(true);
            }}
          >
            Begin
          </Button>
        </DialogActions>
      </Dialog>

      {/* INSTRUCTION DIALOG - Only for AI Mode */}
      <Dialog open={showInstruction} onClose={() => {
        setShowInstruction(false);
        setUnderstood(false);
        setTempSessionData(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Interview Instructions</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <ol>
              <li>
                <strong>No background disturbance.</strong> Find a quiet place, close other apps, and turn off notifications.
              </li>
              <li>
                <strong>10-second rule.</strong> After the bot finishes reading a question you have <strong>exactly 10 seconds</strong> to start speaking. If you don't, the bot will automatically move to the next question.
              </li>
              <li>
                <strong>Finish → next.</strong> When you stop talking the bot will wait a moment and then read the next question.
              </li>
              <li>
                <strong>Repeat request.</strong> Say <em>"repeat the question"</em> (or similar) and the bot will read the current question again.
              </li>
            </ol>
          </DialogContentText>

          <FormControlLabel
            control={
              <Checkbox
                checked={understood}
                onChange={e => setUnderstood(e.target.checked)}
                color="primary"
              />
            }
            label="I have read and understood all the instructions."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowInstruction(false);
            setUnderstood(false);
            setTempSessionData(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!understood}
            onClick={() => {
              setShowInstruction(false);
              onModeSelected(tempSessionData);
              handleCancel(); // Reset everything after starting
            }}
          >
            Start AI Interview
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InterviewModeSelector;